<template>
  <component
    :is="inDepthLayout ? 'Page' : 'product-layout'"
    section="details"
    :productID="$route.params.id"
    :parentID="parentID"
    :isFirstLevel="isFirstLevel"
    :back="backLink"
    :customGradient="inDepthLayout ? 'bg-white' : ''"
    :label="updatedProduct.name"
  >
    <image-gallery :gallery="updatedProduct.images" />
    <detail-section label="Description">
      <div
        class="px-8 font-helvetica text-mid-dark-grey text-16 spacing-1 line-24 mb-4 product-details"
        :style="
          !updatedReadMore
            ? 'display: -webkit-box;-webkit-line-clamp: 3;-webkit-box-orient: vertical;overflow: hidden;text-overflow: ellipsis;'
            : ''
        "
        v-html="formattedDescription"
      />
      <div
        v-if="formattedDescription && formattedDescription.length"
        class="mx-8 mt-4 flex justify-start"
      >
        <section-button
          :label="updatedReadMore ? 'Read less' : 'Read more'"
          @onClick="readMore"
        />
      </div>
    </detail-section>

    <detail-section
      v-if="updatedProduct?.inDepth?.carousel?.items?.length"
      classes="mt-3"
    >
      <home-titled-container
        :label="updatedProduct.inDepth?.carousel?.title"
        classes="overflow-y-visible"
        pt="0"
        :path="`/products/detail/${updatedProduct?.id}/indepth`"
      >
        <carousel id="features-carousel">
          <div
            v-for="(feature, i) in updatedProduct?.inDepth?.carousel?.items"
            class="block"
            :key="i"
            @click="
              () =>
                this.$router.replace({
                  name: 'Products Detail in Depth',
                  params: {
                    id: updatedProduct.id,
                    tab: feature.link.path,
                    anchor: feature.link.anchor,
                  },
                })
            "
          >
            <details-features-slide :index="i" :feature="feature" />
            <div>&nbsp;</div>
          </div>
        </carousel>
      </home-titled-container>
    </detail-section>

    <detail-section v-if="updatedProduct?.benefits?.length">
      <benefits-card :items="updatedProduct.benefits" />
    </detail-section>

    <detail-section v-if="showWhy">
      <div v-if="updatedProduct.why.image" class="px-8">
        <img
          :src="updatedProduct.why.image"
          alt=""
          class="why-image w-full rounded-12 object-cover block"
        />
      </div>
      <small-title
        v-if="updatedProduct.why.title"
        :label="updatedProduct.why.title"
        classes="pt-6 pb-4"
      />
      <read-more-text
        :short="updatedProduct.why.description_short"
        :html="updatedProduct.why.description"
      />
    </detail-section>

    <detail-section
      v-if="updatedProduct.videos && updatedProduct.videos.length"
      label="Videos"
      noSeparator
    >
      <video-gallery :gallery="updatedProduct.videos" />
    </detail-section>
    <detail-section v-if="showDisclaimer" label="Disclaimer">
      <div class="p-8 mx-8 mb-4 bg-light-grey rounded-12">
        <p
          v-for="(disclaimer, i) in updatedProduct.disclaimers"
          :key="i"
          :class="[
            i === 0 || 'mt-4',
            'font-helvetica text-mid-dark-grey text-12 spacing-38 line-20',
          ]"
        >
          {{ disclaimer }}
        </p>
      </div>
    </detail-section>
    <detail-section
      v-if="!!updatedProduct?.relatedProducts?.products?.length"
      label="Related products"
    >
      <div class="p-8 flex flex-col gap-6">
        <div
          v-for="product in updatedProduct?.relatedProducts?.products"
          :key="product?.id"
        >
          <a
            class="flex items-center gap-5 cursor-pointer no-underline"
            @click.prevent="
              () =>
                this.$router.push({
                  path: '/products/detail/' + product.id,
                })
            "
          >
            <ion-img
              :src="product?.image"
              class="object-cover height-56 w-56"
            />
            <p class="flex-1">{{ product?.name }}</p>
          </a>
        </div>
      </div>
    </detail-section>
    <detail-section v-if="showBanner">
      <div class="p-8 mx-8 mb-4 bg-black rounded-12 overflow-hidden">
        <div class="mb-2">
          <Title titleClass="text-white font-bold">{{
            updatedProduct?.banner?.title
          }}</Title>
        </div>

        <p
          class="font-helvetica text-white text-14 spacing-1 line-20 mb-4 product-details"
        >
          {{ updatedProduct?.banner?.description }}
        </p>
        <ion-button
          :href="updatedProduct?.banner?.link"
          type="button"
          class="rounded-8 bg-white text-black flex justify-center line-none items-center w-auto normal-case"
        >
          <span
            style="
               {
                display: inline-block;
                position: relative;
                top: 2px;
              }
            "
          >
            {{ updatedProduct?.banner?.title }}
          </span>
          <ion-img
            slot="end"
            src="/assets/button-icons/external-link.svg"
            class="ml-2"
          />
        </ion-button>
        <ion-img
          :src="updatedProduct?.banner?.banner"
          class="object-cover -mx-8 -mb-8 mt-8"
        />
      </div>
    </detail-section>
  </component>
</template>

<script>
import VideoGallery from "../VideoGallery.vue";
import ImageGallery from "../ImageGallery.vue";
import ProductLayout from "./ProductLayout.vue";
import DetailSection from "../DetailSection.vue";
import SectionButton from "../containers/SectionButton.vue";
import MatomoManager from "../../mixins/MatomoManager.vue";
import { IonButton, IonImg } from "@ionic/vue";
import Page from "../Page.vue";
import Title from "../Title.vue";
import Carousel from "../Carousel.vue";
import HomeTitledContainer from "../home/HomeTitledContainer.vue";
import DetailsFeaturesSlide from "./DetailsFeaturesSlide.vue";
import BenefitsCard from "../BenefitsCard.vue";
import ReadMoreText from "../ReadMoreText.vue";
import SmallTitle from "../SmallTitle.vue";

export default {
  name: "Details",

  components: {
    IonImg,
    ImageGallery,
    VideoGallery,
    SectionButton,
    DetailSection,
    ProductLayout,
    Page,
    Title,
    Carousel,
    DetailsFeaturesSlide,
    HomeTitledContainer,
    IonButton,
    BenefitsCard,
    ReadMoreText,
    SmallTitle,
  },

  props: {
    product: {
      type: Object,
    },
  },

  mixins: [MatomoManager],

  data() {
    return {
      isReadMore: false,
    };
  },

  computed: {
    updatedProduct() {
      return this.product;
    },

    inDepthLayout() {
      return (
        !Array.isArray(this.updatedProduct?.inDepth) &&
        typeof this.updatedProduct?.inDepth === "object" &&
        this.updatedProduct?.inDepth !== null
      );
    },

    showBanner() {
      return (
        !Array.isArray(this.updatedProduct?.banner) &&
        typeof this.updatedProduct?.banner === "object" &&
        this.updatedProduct?.banner !== null
      );
    },

    showWhy() {
      const why = this.updatedProduct?.why;
      return !!why && !Array.isArray(why) && !!(why.title || why.description);
    },

    updatedReadMore() {
      return this.isReadMore;
    },

    formattedDescription() {
      if (
        this.updatedProduct.description &&
        this.updatedProduct.description.length
      ) {
        return this.sanitizeManageText(this.updatedProduct.description);
      }
      return "";
    },

    isFirstLevel() {
      return this.product.parent_first_level;
    },

    parentID() {
      return this.product.parent_id;
    },

    showDisclaimer() {
      return this.product.disclaimers && this.product.disclaimers.length;
    },

    backLink() {
      return [];
    },
  },

  methods: {
    readMore() {
      this.isReadMore = !this.updatedReadMore;
    },
  },
};
</script>

<style scoped>
.why-image {
  height: 221px;
}
</style>
